import { Formik, Form } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { Common } from '@components/Admin';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';

import { formatDate } from '@utils/format';

import { useAdminUpdatePolicy } from 'generated/admin.generated';
import type { PolicyModel } from 'generated/types';

interface MetadataEditorProps {
  policy?: PolicyModel;
  loading?: boolean;
}

const messages = defineMessages({
  name: {
    id: 'admin.policies.name',
    defaultMessage: 'Name',
  },
  description: {
    id: 'admin.policies.description',
    defaultMessage: 'Description',
  },
  updatedAt: {
    id: 'GLOBAL.updatedAt',
    defaultMessage: 'Updated at',
  },
  createdAt: {
    id: 'GLOBAL.createdAt',
    defaultMessage: 'Created At',
  },
});

interface FormValues {
  name?: string | null;
  description?: string | null;
}

function MetadataEditor({ policy, loading: baseLoading }: MetadataEditorProps) {
  const { formatMessage } = useIntl();
  const { AdminSchema } = useCommonSchemas();
  const [updatePolicy, { loading: updatePolicyLoading }] =
    useAdminUpdatePolicy();

  const loading = baseLoading || updatePolicyLoading;

  const initialValues: FormValues = {
    name: policy?.policy_name,
    description: policy?.policy_description,
  };

  const handleSubmit = async (values: FormValues) => {
    if (!policy) {
      return;
    }
    await updatePolicy({
      variables: {
        input: {
          policy_id: policy.policy_id,
          policy_name: values.name ?? undefined,
          policy_description: values.description ?? undefined,
        },
      },
    });
  };

  const schema: Yup.Schema<FormValues> = Yup.object().shape({
    name: AdminSchema.name(1, 128).nullable(),
    description: AdminSchema.description().nullable(),
  });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ values }) => {
        const data: Common.Card.IMetadata.Metadata[] = [
          {
            title: formatMessage(messages.name),
            value: values.name,
            editOptions: { name: 'name', type: 'text' },
          },
          {
            title: formatMessage(messages.description),
            value: values.description,
            editOptions: { name: 'description', type: 'text' },
          },
          {
            title: formatMessage(messages.createdAt),
            value: formatDate({
              date: policy?.created_at,
              formatter: 'datetime',
            }),
          },
          {
            title: formatMessage(messages.updatedAt),
            value: formatDate({
              date: policy?.updated_at,
              formatter: 'datetime',
            }),
          },
        ];
        return (
          <Form>
            <Common.Card.Metadata data={data} loading={loading} />
          </Form>
        );
      }}
    </Formik>
  );
}

export default MetadataEditor;
